// services/metaComissaoService.ts
import { BaseService } from './baseService';
import { MetaComissaoMensal, MetaProdutoComissao } from '../types/entities';
import { apiClient } from '../config/api';
import { ApiResponse, PaginatedResponse, QueryParams, buildQueryString } from '../config/endpoints';

class MetaComissaoService extends BaseService<MetaComissaoMensal> {
  protected endpoints = {
    LIST: '/estatisticas/metas/comissao-mensal',
    CREATE: '/estatisticas/metas/comissao-mensal',
    SEARCH: '/estatisticas/metas/comissao-mensal/search',
    BY_ID: (id: string | number) => `/estatisticas/metas/comissao-mensal/${id}`,
    UPDATE: (id: string | number) => `/estatisticas/metas/comissao-mensal/${id}`,
    DELETE: (id: string | number) => `/estatisticas/metas/comissao-mensal/${id}`
  };

  // ✅ Criar meta mensal com produtos associados
  async createWithProdutos(data: {
    mesAno: string;
    valorPremio?: number;
    valorComissao?: number;
    percentualBase?: number;
    empresaId?: string;
    produtos: Array<{
      produto: string;
      valorObjetivoVenda?: number;
      percentualParticipacao?: number;
    }>;
  }): Promise<ApiResponse<MetaComissaoMensal>> {
    return apiClient.request<ApiResponse<MetaComissaoMensal>>(
      this.endpoints.CREATE,
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    );
  }

  // ✅ Atualizar meta mensal com produtos associados
  async updateWithProdutos(id: string, data: {
    mesAno: string;
    valorPremio?: number;
    valorComissao?: number;
    percentualBase?: number;
    produtos: Array<{
      id?: string;
      produto: string;
      valorObjetivoVenda?: number;
      percentualParticipacao?: number;
    }>;
  }): Promise<ApiResponse<MetaComissaoMensal>> {
    return apiClient.request<ApiResponse<MetaComissaoMensal>>(
      this.endpoints.UPDATE(id),
      {
        method: 'PUT',
        body: JSON.stringify(data)
      }
    );
  }

  // ✅ Listar por período (mês/ano)
  async listByPeriodo(mesAno?: string, empresaId?: string): Promise<PaginatedResponse<MetaComissaoMensal>> {
    const params: any = {};
    if (mesAno) params.mesAno = mesAno;
    if (empresaId) params.empresaId = empresaId;
    
    const queryString = buildQueryString(params);
    return apiClient.request<PaginatedResponse<MetaComissaoMensal>>(
      `${this.endpoints.LIST}${queryString}`
    );
  }

  // ✅ Buscar meta específica com produtos incluídos
  async getWithProdutos(id: string): Promise<{
    meta: MetaComissaoMensal;
    produtos: MetaProdutoComissao[];
  }> {
    try {
      // Buscar meta e produtos separadamente
      const produtoService = new MetaProdutoComissaoService();
      const [metaResponse, produtosResponse] = await Promise.all([
        this.getById(id),
        produtoService.listByMeta(id)
      ]);
      
      return {
        meta: metaResponse.data,
        produtos: produtosResponse.data || []
      };
    } catch (error) {
      console.error('Erro ao buscar meta com produtos:', error);
      throw error;
    }
  }
}

// ✅ Service para produtos da meta
class MetaProdutoComissaoService {
  private baseUrl = '/metas/produto-comissao';

  // ✅ Adicionar produto à meta
  async addProdutoToMeta(data: {
    produto: string;
    percentualParticipacao: number;
    valorObjetivoVenda: number;
    metaMensalId: string;
  }): Promise<ApiResponse<MetaProdutoComissao>> {
    return apiClient.request<ApiResponse<MetaProdutoComissao>>(
      `${this.baseUrl}/produtos`,
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    );
  }

  // ✅ Buscar produto por ID
  async getProdutoById(id: string): Promise<ApiResponse<MetaProdutoComissao>> {
    return apiClient.request<ApiResponse<MetaProdutoComissao>>(
      `${this.baseUrl}/produtos/${id}`
    );
  }

  // ✅ Atualizar produto da meta
  async updateProduto(id: string, data: {
    produto: string;
    valorObjetivoVenda?: number;
    percentualParticipacao?: number;
  }): Promise<ApiResponse<MetaProdutoComissao>> {
    return apiClient.request<ApiResponse<MetaProdutoComissao>>(
      `${this.baseUrl}/produtos/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data)
      }
    );
  }

  // ✅ Remover produto da meta
  async removeProduto(id: string): Promise<ApiResponse<void>> {
    return apiClient.request<ApiResponse<void>>(
      `${this.baseUrl}/produtos/${id}`,
      {
        method: 'DELETE'
      }
    );
  }

  // ✅ Listar produtos de uma meta
  async listByMeta(metaMensalId: string): Promise<ApiResponse<MetaProdutoComissao[]>> {
    return apiClient.request<ApiResponse<MetaProdutoComissao[]>>(
      `${this.baseUrl}/${metaMensalId}/produtos`
    );
  }
}

// ✅ Criar instâncias
const metaComissaoServiceInstance = new MetaComissaoService();
const metaProdutoComissaoServiceInstance = new MetaProdutoComissaoService();

// ✅ Exportar instâncias
export const metaComissaoService = metaComissaoServiceInstance;
export const metaProdutoComissaoService = metaProdutoComissaoServiceInstance;